import { Checkbox, PrimaryButton } from '@systemeio/ui'
import { useBlogCategories } from 'modules/blogs/hooks/use-blog-categories'
import { BlogCategoriesApiInterface } from 'modules/blogs/types/blog-categories-interface'
import React, { useEffect, useState } from 'react'
import {
  FieldErrorAndDescription,
  FieldErrorAndDescriptionProps,
} from 'shared/components/form/field-error-and-description'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { usePagination } from 'shared/route-query/hooks/use-pagination'

interface BlogCategoriesCheckboxProps extends FieldErrorAndDescriptionProps {
  selectedCategories: { id: number; name: string }[]
  onChange: (data: { id: number; name: string }[]) => void
  blogId: string
}

const BlogCategoriesCheckbox = ({
  selectedCategories,
  onChange,
  error,
  blogId,
}: BlogCategoriesCheckboxProps) => {
  const {
    setPagination: setPaginationCategories,
    pagination: paginationCategories,
    limit,
  } = usePagination(true)
  const { blogCategories } = useBlogCategories({
    limit,
    pagination: paginationCategories,
    id: blogId,
  })
  const { t } = useLocoTranslation()
  const [tempState, setTempState] = useState<BlogCategoriesApiInterface>({
    hasMore: false,
    items: [],
  })

  useEffect(() => {
    if (blogCategories) {
      setTempState(prev => {
        const uniqIds = prev.items.map(el => el.id)
        const uniqCategories = blogCategories.items.filter(el => !uniqIds.includes(el.id))
        return {
          hasMore: blogCategories.hasMore,
          items: [...prev.items, ...uniqCategories],
        }
      })
    }
  }, [blogCategories])

  const valueIds = selectedCategories.map(el => el.id)
  return (
    <div
      className={`flex-col gap-1 text-sm font-medium ${
        blogCategories && tempState?.items.length === 0 ? 'hidden' : 'flex'
      }`}
    >
      <span>{t('dashboard.blog.categories.title_plural')}</span>
      <ul className={'flex flex-row flex-wrap gap-5'}>
        {!blogCategories && tempState?.items.length === 0 ? (
          <BlogCategoriesCheckboxSkeleton />
        ) : (
          tempState.items.map(el => {
            const isSelected = valueIds.includes(el.id)
            return (
              <li key={el.id}>
                <Checkbox
                  value={isSelected}
                  label={el.name}
                  onChange={() =>
                    isSelected
                      ? onChange(selectedCategories.filter(category => category.id !== el.id))
                      : onChange([...selectedCategories, el])
                  }
                />
              </li>
            )
          })
        )}
      </ul>
      {tempState.hasMore && (
        <PrimaryButton
          type={'button'}
          isFetching={!blogCategories}
          onClick={() =>
            setPaginationCategories(prev => ({
              ...prev,
              startFromId: tempState.items[tempState.items.length - 1].id,
            }))
          }
        >
          {t('dashboard.blog.categories.load_more')}
        </PrimaryButton>
      )}
      <FieldErrorAndDescription error={error} />
    </div>
  )
}

const BlogCategoriesCheckboxSkeleton = () => (
  <div className={'flex animate-pulse gap-7'}>
    <div className={'bg-skeleton-primary/40 h-[23px] rounded-lg px-7 text-transparent'}>.</div>
    <div className={'bg-skeleton-primary/40 h-[23px] rounded-lg px-7 text-transparent'}>.</div>
    <div className={'bg-skeleton-primary/40 h-[23px] rounded-lg px-7 text-transparent'}>.</div>
  </div>
)

export default BlogCategoriesCheckbox
